import { AlertCircle, Info } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { FormQuestion, QuestionValidation } from './types';

interface ValidationDisplayProps {
  question: FormQuestion;
  error?: string;
  showHints?: boolean;
  className?: string;
}

type ValidationRule = QuestionValidation['rules'] extends Array<infer Item> ? Item : never;

const formatLengthHint = (rule: Extract<ValidationRule, { kind: 'length' }>) => {
  if (rule.min && rule.max) {
    return `Must be between ${rule.min} and ${rule.max} characters`;
  }
  if (rule.min) {
    return `Must be at least ${rule.min} characters`;
  }
  if (rule.max) {
    return `Must be ${rule.max} characters or fewer`;
  }
  return undefined;
};

const formatPatternHint = (rule: Extract<ValidationRule, { kind: 'pattern' }>) => {
  if (rule.message) {
    return rule.message;
  }
  return 'Must match the required format';
};

const formatNumericHint = (rule: Extract<ValidationRule, { kind: 'numeric' }>) => {
  if (rule.min !== undefined && rule.max !== undefined) {
    return `Value must be between ${rule.min} and ${rule.max}`;
  }
  if (rule.min !== undefined) {
    return `Value must be at least ${rule.min}`;
  }
  if (rule.max !== undefined) {
    return `Value must be ${rule.max} or less`;
  }
  return undefined;
};

const formatFileHint = (rule: Extract<ValidationRule, { kind: 'file' }>) => {
  const parts: string[] = [];
  if (rule.maxSizeMb) {
    parts.push(`Up to ${rule.maxSizeMb} MB`);
  }
  if (rule.accept?.length) {
    parts.push(`Accepted: ${rule.accept.join(', ')}`);
  }
  return parts.length ? parts.join(' • ') : undefined;
};

const buildQuestionSpecificHints = (question: FormQuestion) => {
  const hints: string[] = [];

  const isDisplayOnly =
    question.type === "read_only_text" ||
    question.type === "link" ||
    question.type === "rich_text" ||
    question.type === "image" ||
    question.type === "divider" ||
    question.type === "callout";

  if (!isDisplayOnly && (question.required || question.validation?.required)) {
    hints.push('Response required');
  }

  switch (question.type) {
    case 'multiple_choice_multi': {
      if (question.minSelections) {
        hints.push(`Select at least ${question.minSelections} option${question.minSelections > 1 ? 's' : ''}`);
      }
      if (question.maxSelections) {
        hints.push(`Select up to ${question.maxSelections} option${question.maxSelections > 1 ? 's' : ''}`);
      }
      break;
    }
    case 'file_upload': {
      hints.push(`Upload up to ${question.maxFiles ?? 1} file${(question.maxFiles ?? 1) > 1 ? 's' : ''}`);
      hints.push(`Max size: ${(question.maxSizeMb ?? 10).toLocaleString()} MB each`);
      if (question.accept?.length) {
        hints.push(`Accepted types: ${question.accept.join(', ')}`);
      }
      break;
    }
    case 'signature': {
      if (question.requireTypedName) {
        hints.push('Signature must include a typed name');
      }
      break;
    }
    case 'phone_number': {
      if (question.defaultCountry) {
        hints.push(`Default country: ${question.defaultCountry.toUpperCase()}`);
      }
      if (question.allowInternational === false) {
        hints.push('International numbers are not accepted');
      }
      break;
    }
    default: {
      break;
    }
  }

  return hints;
};

const buildValidationHints = (question: FormQuestion): string[] => {
  const hints = new Set<string>();

  buildQuestionSpecificHints(question).forEach((hint) => hints.add(hint));

  const rules = question.validation?.rules ?? [];

  rules.forEach((rule) => {
    switch (rule.kind) {
      case 'length': {
        const hint = formatLengthHint(rule);
        if (hint) hints.add(hint);
        break;
      }
      case 'pattern': {
        const hint = formatPatternHint(rule);
        if (hint) hints.add(hint);
        break;
      }
      case 'numeric': {
        const hint = formatNumericHint(rule);
        if (hint) hints.add(hint);
        break;
      }
      case 'file': {
        const hint = formatFileHint(rule);
        if (hint) hints.add(hint);
        break;
      }
      default: {
        break;
      }
    }
  });

  return Array.from(hints);
};

export const ValidationDisplay = ({ question, error, showHints, className }: ValidationDisplayProps) => {
  const hints = showHints ? buildValidationHints(question) : [];
  const hasHints = hints.length > 0;
  const hasError = Boolean(error);
  if (!hasHints && !hasError) {
    return null;
  }

  const validationId = `validation-${question.id}`;
  const errorId = `error-${question.id}`;

  return (
    <div id={validationId} className={cn('space-y-2', className)}>
      {showHints && hasHints ? (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {hints.map((hint) => (
            <li key={hint} className="flex items-start gap-2">
              <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground/70" aria-hidden="true" />
              <span>{hint}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {hasError ? (
        <p id={errorId} role="alert" aria-live="assertive" className="flex items-start gap-2 text-xs font-medium text-destructive">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
};
